import { useEffect, useRef, useState } from 'react'
import type { GeoPoint } from '../application/tracking/geo'
import { fetchStreetPath, type StreetPath } from '../infrastructure/mapbox/streetPath'

export type SuggestedPathState =
  | { status: 'off' }
  | { status: 'loading' }
  | { status: 'ready'; route: StreetPath }
  | { status: 'unavailable' }

/** Environ 110 m : le trajet n'est redemandé qu'après un vrai déplacement ou un changement de résidence. */
const originStepDeg = 0.001

function cell(point: GeoPoint): string {
  return `${String(Math.round(point.lat / originStepDeg))}:${String(Math.round(point.lng / originStepDeg))}`
}

/**
 * Trajet par les rues de la tablette à la résidence suggérée. Un appel Mapbox par résidence visée et par cellule
 * d'environ 110 m traversée, jamais à chaque relevé GPS.
 */
export function useSuggestedPath(
  token: string | undefined,
  enabled: boolean,
  origin: GeoPoint | null,
  target: { id: string; point: GeoPoint } | null,
): SuggestedPathState {
  const [result, setResult] = useState<{ key: string; targetId: string; route: StreetPath | null } | null>(null)
  const originRef = useRef(origin)
  const targetRef = useRef(target)
  const key = token && enabled && origin && target ? `${target.id}@${cell(origin)}` : null

  useEffect(() => {
    originRef.current = origin
    targetRef.current = target
  })

  useEffect(() => {
    const from = originRef.current
    const to = targetRef.current
    if (!key || !token || !from || !to) return
    const controller = new AbortController()
    void fetchStreetPath(from, to.point, token, controller.signal).then((route) => {
      if (!controller.signal.aborted) setResult({ key, targetId: to.id, route })
    })
    return () => {
      controller.abort()
    }
  }, [key, token])

  if (!key) return { status: 'off' }
  // Même résidence, nouvel endroit : l'ancien trajet reste affiché pendant le calcul. Jamais celui d'une autre résidence.
  if (result?.key !== key) {
    return result?.route && result.targetId === target?.id ? { status: 'ready', route: result.route } : { status: 'loading' }
  }
  return result.route ? { status: 'ready', route: result.route } : { status: 'unavailable' }
}
